import { PageViewEvent } from '@shopify/events';
import { onDocumentReady } from '@theme/utilities';

onDocumentReady(
  function dispatchPageViewEvent() {
    /** @type {HTMLElement | null} */
    const templateElement = document.querySelector('main[data-template]');
    const template = templateElement?.dataset.template || '';

    // -- Page --
    document.dispatchEvent(
      new PageViewEvent({
        page: {
          template,
          title: document.title,
          url: window.location.href,
        },
      })
    );
  },
  { once: true }
);

// Patogus verslas: consent-aware first/last-touch attribution for lead forms.
const PV_ATTRIBUTION_KEY = 'pv_attribution_v1';
const PV_ATTRIBUTION_TTL = 30 * 24 * 60 * 60 * 1000;

function pvMarketingAllowed() {
  try {
    return Boolean(window.Shopify?.customerPrivacy?.marketingAllowed?.());
  } catch (error) {
    return false;
  }
}

function pvClearAttribution() {
  try {
    window.localStorage.removeItem(PV_ATTRIBUTION_KEY);
  } catch (error) {
    // Storage can be unavailable in private/restricted browser modes.
  }
}

function pvReadAttribution() {
  if (!pvMarketingAllowed()) return null;

  try {
    const raw = window.localStorage.getItem(PV_ATTRIBUTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > PV_ATTRIBUTION_TTL) {
      pvClearAttribution();
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
}

function pvExternalReferrerHost() {
  if (!document.referrer) return '';

  try {
    const referrerHost = new URL(document.referrer).hostname.toLowerCase();
    const currentHost = window.location.hostname.toLowerCase();

    if (!referrerHost || referrerHost === currentHost) return '';
    if (referrerHost === 'admin.shopify.com') return '';
    if (referrerHost.endsWith('.myshopify.com') || referrerHost.endsWith('.shopify.com')) return '';

    return referrerHost;
  } catch (error) {
    return '';
  }
}

function pvIsGoogleHost(host) {
  return Boolean(host && (host.startsWith('google.') || host.includes('.google.')));
}

function pvCurrentTouch() {
  const params = new URLSearchParams(window.location.search);
  const externalReferrer = pvExternalReferrerHost();
  const utmSource = params.get('utm_source') || '';
  const utmMedium = params.get('utm_medium') || '';
  const utmCampaign = params.get('utm_campaign') || '';
  const utmTerm = params.get('utm_term') || '';
  const utmContent = params.get('utm_content') || '';
  const clickIds = [
    ['gclid', params.get('gclid')],
    ['gbraid', params.get('gbraid')],
    ['wbraid', params.get('wbraid')],
    ['msclkid', params.get('msclkid')],
  ];
  const click = clickIds.find((entry) => entry[1]);
  const clickType = click?.[0] || '';
  const clickId = click?.[1] || '';
  const hasUtm = Boolean(utmSource || utmMedium || utmCampaign || utmTerm || utmContent);

  let source = utmSource;
  let medium = utmMedium;

  if (!source && ['gclid', 'gbraid', 'wbraid'].includes(clickType)) source = 'google';
  if (!source && clickType === 'msclkid') source = 'bing';

  if (!medium && clickId) medium = 'cpc';

  if (!source && externalReferrer) {
    if (pvIsGoogleHost(externalReferrer)) {
      source = 'google';
      medium = medium || 'organic';
    } else if (externalReferrer.includes('bing.com')) {
      source = 'bing';
      medium = medium || 'organic';
    } else {
      source = externalReferrer;
      medium = medium || 'referral';
    }
  }

  if (!source) source = 'direct';
  if (!medium) medium = source === 'direct' ? '(none)' : 'campaign';

  return {
    source,
    medium,
    campaign: utmCampaign,
    term: utmTerm,
    content: utmContent,
    clickType,
    clickId,
    landing: window.location.pathname,
    referrer: externalReferrer,
    capturedAt: new Date().toISOString(),
    hasAcquisitionSignal: Boolean(hasUtm || clickId || externalReferrer),
  };
}

function pvSaveCurrentTouch() {
  if (!pvMarketingAllowed()) {
    pvClearAttribution();
    return null;
  }

  const current = pvCurrentTouch();
  const existing = pvReadAttribution();
  const data = existing || {
    first: current,
    last: current,
    savedAt: Date.now(),
  };

  if (!data.first) data.first = current;
  if (!data.last || current.hasAcquisitionSignal) data.last = current;
  data.savedAt = Date.now();

  try {
    window.localStorage.setItem(PV_ATTRIBUTION_KEY, JSON.stringify(data));
  } catch (error) {
    return data;
  }

  document.dispatchEvent(new CustomEvent('pv:attribution-ready'));
  return data;
}

function pvTouchLabel(touch) {
  if (!touch) return '';
  return `${touch.source || ''} / ${touch.medium || ''}${touch.campaign ? ` / ${touch.campaign}` : ''}`;
}

function pvTouchDetails(touch) {
  if (!touch) return 'UTM nėra';
  const values = [];
  if (touch.source) values.push(`source=${touch.source}`);
  if (touch.medium) values.push(`medium=${touch.medium}`);
  if (touch.campaign) values.push(`campaign=${touch.campaign}`);
  if (touch.term) values.push(`term=${touch.term}`);
  if (touch.content) values.push(`content=${touch.content}`);
  return values.length ? values.join(' | ') : 'UTM nėra';
}

function pvEnsureHiddenField(form, name, id) {
  let field = document.getElementById(id);
  if (field) return field;

  field = document.createElement('input');
  field.type = 'hidden';
  field.name = name;
  field.id = id;
  form.appendChild(field);
  return field;
}

function pvPopulateQuoteForm() {
  const form = document.getElementById('PVQuoteForm');
  if (!form) return;

  const pageField = document.getElementById('PVQuotePageUrl');
  const referrerField = document.getElementById('PVQuoteReferrer');
  const utmField = document.getElementById('PVQuoteUtm');
  const firstSourceField = pvEnsureHiddenField(form, 'contact[Pirmas šaltinis]', 'PVQuoteFirstSource');
  const lastSourceField = pvEnsureHiddenField(form, 'contact[Paskutinis šaltinis]', 'PVQuoteLastSource');
  const firstLandingField = pvEnsureHiddenField(form, 'contact[Pirmas landingas]', 'PVQuoteFirstLanding');
  const lastLandingField = pvEnsureHiddenField(form, 'contact[Paskutinis landingas]', 'PVQuoteLastLanding');
  const campaignField = pvEnsureHiddenField(form, 'contact[Kampanija]', 'PVQuoteCampaign');
  const clickIdField = pvEnsureHiddenField(form, 'contact[Reklamos click ID]', 'PVQuoteClickId');

  if (pageField) pageField.value = `${window.location.origin}${window.location.pathname}`;

  if (!pvMarketingAllowed()) {
    if (referrerField) referrerField.value = '';
    if (utmField) utmField.value = '';
    firstSourceField.value = '';
    lastSourceField.value = '';
    firstLandingField.value = '';
    lastLandingField.value = '';
    campaignField.value = '';
    clickIdField.value = '';
    return;
  }

  const data = pvReadAttribution() || pvSaveCurrentTouch();
  if (!data) return;

  if (referrerField) referrerField.value = pvTouchLabel(data.first);
  if (utmField) utmField.value = pvTouchDetails(data.last);
  firstSourceField.value = pvTouchLabel(data.first);
  lastSourceField.value = pvTouchLabel(data.last);
  firstLandingField.value = data.first?.landing || '';
  lastLandingField.value = data.last?.landing || '';
  campaignField.value = data.last?.campaign || data.first?.campaign || '';

  const clickTouch = data.last?.clickId ? data.last : data.first;
  clickIdField.value = clickTouch?.clickId ? `${clickTouch.clickType}=${clickTouch.clickId}` : '';
}

function pvSetupQuoteForm() {
  const form = document.getElementById('PVQuoteForm');
  if (!form) return;

  pvPopulateQuoteForm();
  form.addEventListener('submit', pvPopulateQuoteForm);
  document.addEventListener('pv:attribution-ready', pvPopulateQuoteForm);
}

function pvLoadPrivacyApi(attempt = 0) {
  if (!window.Shopify?.loadFeatures) {
    if (attempt < 20) window.setTimeout(() => pvLoadPrivacyApi(attempt + 1), 250);
    return;
  }

  window.Shopify.loadFeatures(
    [{ name: 'consent-tracking-api', version: '0.1' }],
    (error) => {
      if (error || !window.Shopify?.customerPrivacy) return;

      if (pvMarketingAllowed()) {
        pvSaveCurrentTouch();
      } else {
        pvClearAttribution();
      }
      pvPopulateQuoteForm();

      document.addEventListener('visitorConsentCollected', () => {
        if (pvMarketingAllowed()) {
          pvSaveCurrentTouch();
        } else {
          pvClearAttribution();
          pvPopulateQuoteForm();
        }
      });
    }
  );
}

window.PVAttribution = {
  get() {
    return pvReadAttribution();
  },
  refresh() {
    return pvSaveCurrentTouch();
  },
};

onDocumentReady(pvSetupQuoteForm, { once: true });
pvLoadPrivacyApi();

// Patogus verslas: publish non-PII lead/contact interactions to Shopify Customer Events.
function pvPublishCustomEvent(name, data = {}) {
  try {
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (error) {
    // Measurement must never interrupt the buyer journey.
  }
}

function pvSetupLeadAndContactEvents() {
  const quoteForm = document.getElementById('PVQuoteForm');
  if (quoteForm) {
    quoteForm.addEventListener('submit', () => {
      const need = document.getElementById('PVQuoteNeed')?.value || '';
      const activity = document.getElementById('PVQuoteActivity')?.value || '';

      pvPublishCustomEvent('patogus_verslas:quote_submit', {
        path: window.location.pathname,
        need,
        activity,
      });
    });
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link) return;

    const href = link.getAttribute('href') || '';
    let contactType = '';

    if (href.startsWith('tel:')) contactType = 'phone';
    else if (href.startsWith('mailto:')) contactType = 'email';
    else if (/wa\.me|api\.whatsapp\.com|whatsapp:/i.test(href)) contactType = 'whatsapp';

    if (!contactType) return;

    pvPublishCustomEvent('patogus_verslas:contact_click', {
      path: window.location.pathname,
      contactType,
    });
  });
}

onDocumentReady(pvSetupLeadAndContactEvents, { once: true });
